import { CommonModule, TitleCasePipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import _ from 'lodash';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { CheckboxModule } from 'primeng/checkbox';
import { DatepickerComponent } from '../../../@core/components/ui/datepicker/datepicker.component';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { PaginatorModule } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { StepperModule } from 'primeng/stepper';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, Subject } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { MultiSelectComponent } from '../../../@core/components/ui/multi-select';
import { SortPopoverComponent, SortFieldOption } from '../../../@shared/components/sort-popover/sort-popover.component';
import { ProjectActionsMenuComponent } from '../../../@shared/components/project-actions-menu/project-actions-menu.component';
import { SelectButtonComponent } from '../../../@core/components/ui/select-button/select-button.component';
import { PROJECT_KNOWLEDGE_BASE, USER_ROLE, USER_TYPE } from '../../../@core/models/enums';
import { ICustomer, IMilestone, IProject, IShareUser, IStatus, IUser, MILESTONE_TYPE, SHARE_ACCESS, SHARE_FEATURE } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { AvatarStackComponent } from '../../../@shared/components/avatar-stack/avatar-stack.component';
import { GridListViewComponent } from '../../../@shared/components/grid-list-view/grid-list-view.component';
import { ProjectFormDialogComponent } from '../../../@shared/components/project-form-dialog/project-form-dialog.component';
import { SearchBarComponent } from '../../../@shared/components/search-bar/search-bar.component';
import { TableColumn, TableConfig } from '../../../@shared/models/table.models';
import { MileStoneService } from '../../services/milestone.service';
import { IProjectFilter, ProjectService } from '../../services/project.service';
import { ShareService } from '../../services/share.service';
import { UserService } from '../../services/user.service';
import { DASHBOARD_SCREEN } from '../../models/contract-management.model';
import { MilestonesComponent } from '../milestones/milestones.component';
import { PROJECTS_TABLE_COLUMNS, PROJECTS_TABLE_CONFIG } from './projects-table.config';
import { SegmentedOption } from 'src/app/@core/components/ui/segmented/segmented.component';
import { DeleteConfirmationComponent } from '../../../@core/components/ui/delete-confirmation/delete-confirmation.component';

@Component({
  selector: 'app-projects',
  imports: [
    ButtonComponent,
    SelectButtonComponent,
    InputGroupModule,
    InputGroupAddonModule,
    CommonModule,
    FormsModule,
    DialogModule,
    SelectModule,
    DatepickerComponent,
    CheckboxModule,
    DrawerModule,
    ProgressBarModule,
    TagModule,
    AvatarModule,
    PaginatorModule,
    ProjectFormDialogComponent,
    GridListViewComponent,
    StepperModule,
    SkeletonModule,
    AvatarGroupModule,
    TooltipModule,
    SearchBarComponent,
    AvatarGroupModule,
    TooltipModule,
    TitleCasePipe,
    AvatarStackComponent,
    MultiSelectComponent,
    RouterOutlet,
    SortPopoverComponent,
    ProjectActionsMenuComponent,
    DeleteConfirmationComponent
  ],
  standalone: true,
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss'
})
export class ProjectsComponent implements OnInit, AfterViewInit, OnDestroy {

  active: boolean = true;
  @Output() projectsCountChange = new EventEmitter<{ key: DASHBOARD_SCREEN, count: number }>();

  projects: IProject[] = [];

  // view/sort state
  viewMode: 'grid' | 'list' = 'grid';

  // View mode options for select-button
  viewModeOptions: SegmentedOption[] = [
    {
      label: 'List',
      value: 'list',
      icon: 'pi pi-bars'
    },
    {
      label: 'Grid',
      value: 'grid',
      icon: 'pi pi-th-large'
    }
  ];
  sortField: 'name' | 'createdAt' | 'dueDate' | 'updatedAt' = 'updatedAt';
  sortOrder: 'asc' | 'desc' = 'desc';
  readonly sortFields: SortFieldOption<'name' | 'createdAt' | 'dueDate' | 'updatedAt'>[] = [
    { label: 'Project Name', value: 'name' },
    { label: 'Date Created', value: 'createdAt' },
    { label: 'Due Date', value: 'dueDate' },
    { label: 'Updated At', value: 'updatedAt' },
  ];

  // filter state
  projectSearchValue = '';
  filterProjectName = '';
  filterCustomerName = '';
  filterManager: string[] = [];
  filterDueDateRange: Date[] | null = null;
  filterCreatedDateRange: Date[] | null = null;
  filterPanelVisible = false;
  appliedFilters: {
    projectSearchValue: string;
    filterProjectName: string;
    filterCustomerName: string;
    filterManager: string[];
    filterDueDateRange: Date[] | null;
    filterCreatedDateRange: Date[] | null;
  } = {
      projectSearchValue: '',
      filterProjectName: '',
      filterCustomerName: '',
      filterManager: [],
      filterDueDateRange: null,
      filterCreatedDateRange: null,
    };
  // dialog + stepper state
  projectDialogVisible = false;
  @ViewChild(ProjectFormDialogComponent) projectFormComp?: ProjectFormDialogComponent;
  @ViewChild(MilestonesComponent) milestonesComp?: MilestonesComponent;
  projectFormData: Partial<IProject> = {
    projectId: '',
    name: '',
    managers: [],
    customer: '',
    dueDate: undefined,
    internal: false
  };

  createProjectMilestones: IMilestone[] = [];

  managerOptions: { label: string; value: string; email: string, user: IUser }[] = [];

  // Pagination state
  defaultPaginatorValues = {
    first: 0,
    rows: 20
  };
  first: number = this.defaultPaginatorValues.first;
  rows: number = this.defaultPaginatorValues.rows;
  totalRecords: number = 0;


  // Template References
  @ViewChild('projectNameTemplateRef') projectNameTemplateRef!: TemplateRef<any>;
  @ViewChild('customerTemplateRef') customerTemplateRef!: TemplateRef<any>;
  @ViewChild('upcomingMilestoneTemplateRef') upcomingMilestoneTemplateRef!: TemplateRef<any>;
  @ViewChild('managerTemplateRef') managerTemplateRef!: TemplateRef<any>;
  @ViewChild('statusTemplateRef') statusTemplateRef!: TemplateRef<any>;
  @ViewChild('dueDateTemplateRef') dueDateTemplateRef!: TemplateRef<any>;
  @ViewChild('dateCreatedTemplateRef') dateCreatedTemplateRef!: TemplateRef<any>;
  @ViewChild('actionsTemplateRef') actionsTemplateRef!: TemplateRef<any>;

  editProjectId: string | null = null;
  displayDeleteDialog = false;
  projectToDelete: IProject | null = null;

  // Share-to-users state
  currentUser: IUser | null | undefined = null;

  // Table Configuration
  tableColumns: TableColumn[] = [...PROJECTS_TABLE_COLUMNS];
  tableConfig: TableConfig = { ...PROJECTS_TABLE_CONFIG };
  shareDetails: { users: IShareUser[]; anyone: SHARE_ACCESS; workspaceShare: boolean; projectShare: boolean } = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };
  isAdmin: boolean = false;
  isWorkspaceAdmin: boolean = false;
  isChildRouteActive: boolean = false;

  permissions: {
    canView: boolean,
    canEdit: boolean,
    canComment: boolean,
    restricted: boolean
  } = {
      canView: false,
      canEdit: false,
      canComment: false,
      restricted: true
    }

  // Subscription management
  private destroy$ = new Subject<void>();

  //#region LOADING STATES
  isCreateProjectLoading = signal<boolean>(false);
  isUpdateProjectLoading = signal<boolean>(false);
  isDownloadingZIP = signal<boolean>(false);
  //#endregion

  constructor(
    private projectService: ProjectService,
    private userService: UserService,
    private toast: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private milestoneService: MileStoneService,
    private messageService: MessageService,
    private authService: AuthenticationService,
    private shareService: ShareService
  ) {
    this.authService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.currentUser = res;
        this.isAdmin = this.currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role) : false
        this.isWorkspaceAdmin = this.currentUser?.workspaceRole === SHARE_ACCESS.ADMIN
      });

    // Detect when child route is active
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isChildRouteActive = this.route.children.length > 0;
      });
  }

  ngOnInit() {
    // Check initial child route state
    this.isChildRouteActive = this.route.children.length > 0;

    this.loadManagers();
    this.getStandardMilestones();

    // Subscribe to query params to handle 'active' and 'create' parameters
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        // Handle active param (default to true if not provided)
        const activeParam = params['active'];
        this.active = activeParam === 'false' ? false : true;

        // Load projects with the active state
        this.getProjects(this.first, this.rows);

        // Check if we should automatically open the create project dialog
        if (params['create'] === 'true') {
          // Open the dialog first
          setTimeout(() => {
            this.openProjectDialog();
            // Remove the query param from URL after dialog is opened
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { active: activeParam },
              queryParamsHandling: 'replace',
              replaceUrl: true
            });
          }, 100);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    // Build templates object for data table
    this.buildTemplatesMap();
  }

  private getStandardMilestones() {
    this.milestoneService.getMilestones(MILESTONE_TYPE.PROJECT).subscribe(res => {
      if (res.success) {
        this.createProjectMilestones = res.data.map(m => {
          delete m._id;
          return {
            ...m,
            date: new Date()
          }
        });
      }
    });
  }

  get templatesMap(): { [key: string]: TemplateRef<any> } {
    return {
      'projectNameTemplate': this.projectNameTemplateRef,
      'customerTemplate': this.customerTemplateRef,
      'upcomingMilestoneTemplate': this.upcomingMilestoneTemplateRef,
      'managerTemplate': this.managerTemplateRef,
      'statusTemplate': this.statusTemplateRef,
      'dueDateTemplate': this.dueDateTemplateRef,
      'dateCreatedTemplate': this.dateCreatedTemplateRef,
      'actionsTemplate': this.actionsTemplateRef
    };
  }

  private buildTemplatesMap() {
    // This method can be used for any additional template setup if needed
  }

  private loadManagers() {
    const filter = {
      role: [SHARE_ACCESS.ADMIN, SHARE_ACCESS.EDIT, SHARE_ACCESS.VIEW, SHARE_ACCESS.COMMENT],
      includeAdmins: false
    }
    this.userService.getWorkspaceUsers(filter).subscribe(res => {
      if (res.success) {
        this.managerOptions = res.data.map((u: IUser) => ({
          label: u.fullName,
          email: u.email,
          value: u._id,
          user: u
        })) as { label: string; email: string; value: string; user: IUser }[];
      }
    });
  }

  getProjects(skip = this.defaultPaginatorValues.first, limit = this.defaultPaginatorValues.rows) {
    const filters: IProjectFilter = {
      searchValue: this.filterProjectName,
      customerName: this.filterCustomerName,
      managerIds: this.filterManager,
      dueDate: this.rangeToIso(this.filterDueDateRange),
      createdDate: this.rangeToIso(this.filterCreatedDateRange),
      active: this.active,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    };

    this.tableConfig = { ...this.tableConfig, loading: true };

    this.projectService.getProjects(skip, limit, filters).subscribe(res => {
      if (res.success) {
        const projects = res.data.projects.map(p => p.dueDate ? { ...p, dueDateExceeding: moment(p.dueDate).startOf('day').isBefore(moment().endOf('day')) } : p)
        this.projects = projects;
        this.totalRecords = res.data.totalRecords;
        this.projectsCountChange.emit({ key: this.active ? DASHBOARD_SCREEN.PROJECTS : DASHBOARD_SCREEN.ARCHIVED, count: this.totalRecords });
        this.sortProjects();
        this.tableConfig = { ...this.tableConfig, loading: false };
      } else {
        this.toast.error('Error fetching projects');
        this.tableConfig = { ...this.tableConfig, loading: false };
      }
    });
  }

  private rangeToIso(range: Date[] | null) {
    return {
      from: range?.[0]?.toISOString() || '',
      to: range?.[1]?.toISOString() || ''
    };
  }

  // called by search input (bind to (input)="onProjectSearch()")
  onProjectSearch() {
    this.filterProjectName = this.projectSearchValue;
    this.first = 0; // Reset to first page when searching
    this.getProjects(this.first, this.rows);
  }

  // Dialog
  openProjectDialog() {
    this.editProjectId = null;
    // Reset share details fresh for each open
    this.shareDetails = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };
    if (this.currentUser) {
      this.shareDetails.users.push({
        name: this.currentUser.fullName as string,
        user: this.currentUser as IUser,
        email: (this.currentUser as IUser).email as string,
        type: USER_TYPE.INTERNAL,
        role: [SHARE_ACCESS.OWNER],
      } as IShareUser);
    }
    this.loadManagers();
    this.projectDialogVisible = true;
  }

  resetProjectDialog() {
    this.editProjectId = null;
    this.projectFormData = {
      projectId: '',
      name: '',
      managers: [],
      customer: '',
      dueDate: undefined,
      internal: false
    };
    this.createProjectMilestones = [];
  }

  private getMilestonesData() {
    if (this.milestonesComp) {
      this.createProjectMilestones = this.milestonesComp.editableMilestones() as IMilestone[];
    }
  }

  giveAccessToUsers(projectId: string, needAccessUsers: { user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }[]) {
    let userCount = 0;
    for (const user of needAccessUsers) {
      this.shareService.giveAccessToShareUser({
        name: user.user?.fullName ?? (user.user?.firstName + ' ' + user.user?.lastName),
        user: user.user._id,
        email: user.user.email ?? '',
        type: USER_TYPE.INTERNAL as any,
        role: user.shareUser.role,
      }, projectId, SHARE_FEATURE.PROJECT).subscribe(res => {
        if (res.data.hasAccess) {
          this.toast.success('User ' + user.user.fullName + ' Added to Workspace successfully');
        }
      })
    }
  }

  get canCreateProjects() {
    if (!this.currentUser) return false;
    const createPermssions = { canEdit: true, canComment: true, canView: true, restricted: false };
    if ([SHARE_ACCESS.OWNER, SHARE_ACCESS.ADMIN].includes(this.currentUser.workspaceRole)) {
      this.permissions = createPermssions;
      return true;
    }
    if (this.currentUser.currentWorkspace && this.currentUser.workspaceRole &&
      ![SHARE_ACCESS.OWNER, SHARE_ACCESS.ADMIN].includes(this.currentUser.workspaceRole)) {
      return false;
    }
    if (!this.currentUser.currentWorkspace && [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role)) {
      this.permissions = createPermssions;
      return true;
    }
    return false;
  }


  onCreateProject(event: { formData: Partial<IProject>, milestones: IMilestone[], deletedMilestones: string[], teamMembers: IShareUser[], workspaceShare: boolean, projectShare: boolean, needAccessUsers?: { user: Partial<IUser>, shareUser: Partial<IShareUser>, hasAccess: boolean }[], projectKnowledge: { files: File[], knowledgeBases: PROJECT_KNOWLEDGE_BASE[] } }) {
    if (this.isCreateProjectLoading()) {
      return;
    }
    this.isCreateProjectLoading.set(true);
    const payload = {
      project: event.formData,
      milestones: event.milestones,
      deletedMilestones: event.deletedMilestones,
      teamMembers: event.teamMembers,
      workspaceShare: event.workspaceShare,
      projectShare: event.projectShare,
      projectKnowledge: event.projectKnowledge
    };

    this.projectService.saveProject(payload)
      .pipe(finalize(() => this.isCreateProjectLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (!res.success) {
            this.toast.error('Failed to create project.');
            return;
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Project saved!',
            detail: 'Project has been created successfully.'
          });
          this.projectDialogVisible = false;
          this.resetProjectDialog();
          this.projectService.projectsUpdated.next();
          if (event.needAccessUsers && event.needAccessUsers.length > 0) {
            this.giveAccessToUsers(res.data._id, event.needAccessUsers);
          }
          this.router.navigate(['/projects'], {
            queryParams: {
              projectId: res.data._id,
              newProject: 'true'
            }
          });
        },
        error: () => {
          this.toast.error('Failed to create project.');
        }
      });
  }

  // Sort
  setSortField(field: 'name' | 'createdAt' | 'dueDate' | 'updatedAt') {
    this.sortField = field;
    this.sortProjects();
  }

  setSortOrder(order: 'asc' | 'desc') {
    this.sortOrder = order;
    this.sortProjects();
  }

  sortProjects() {
    this.projects = _.orderBy(
      this.projects,
      [p => (this.sortField === 'name'
        ? p.name?.toLowerCase()
        : new Date(p[this.sortField]).getTime())],
      [this.sortOrder]
    );
  }

  // Filters
  toggleFilterPanel(close = false) {
    if (close) {
      // fall back to last applied filters
      this.filterProjectName = this.appliedFilters.filterProjectName || '';
      this.filterCustomerName = this.appliedFilters.filterCustomerName || '';
      this.filterManager = this.appliedFilters.filterManager || [];
      this.filterDueDateRange = this.appliedFilters.filterDueDateRange || null;
      this.filterCreatedDateRange = this.appliedFilters.filterCreatedDateRange || null;
      this.projectSearchValue = this.appliedFilters.projectSearchValue || '';
    }
    this.filterPanelVisible = !this.filterPanelVisible;
  }

  clearAllFilters() {
    this.projectSearchValue = '';
    this.filterProjectName = '';
    this.filterCustomerName = '';
    this.filterManager = [];
    this.filterDueDateRange = null;
    this.filterCreatedDateRange = null;
    this.first = 0;
    this.appliedFilters = {
      projectSearchValue: '',
      filterProjectName: '',
      filterCustomerName: '',
      filterManager: [],
      filterDueDateRange: null,
      filterCreatedDateRange: null,
    };
    this.first = 0;
    this.toggleFilterPanel();
    this.getProjects(this.first, this.rows);
  }

  applyFilters() {
    this.first = 0; // Reset to first page when applying filters
    this.appliedFilters = {
      projectSearchValue: this.projectSearchValue,
      filterProjectName: this.filterProjectName,
      filterCustomerName: this.filterCustomerName,
      filterManager: this.filterManager,
      filterDueDateRange: this.filterDueDateRange,
      filterCreatedDateRange: this.filterCreatedDateRange,
    };
    this.getProjects(this.first, this.rows);
    this.filterPanelVisible = false;
  }

  onPageChange(event: any) {
    this.first = event.first ?? this.defaultPaginatorValues.first;
    this.rows = event.rows ?? this.defaultPaginatorValues.rows;
    this.getProjects(this.first, this.rows);
  }

  // Helpers
  getStatusColor(status: IProject['status']): string {
    const projectStatus = status as IStatus;
    switch (projectStatus.name) {
      case 'Open': return 'bg-blue-100 text-blue-800';
      case 'Completed': return 'bg-green-100 text-green-600';
      case 'Overdue': return 'bg-red-100 text-red-600';
      case 'Archived': return 'bg-purple-100 text-purple-600'
      default: return 'bg-blue-100 text-blue-800';
    }
  }

  getStatus(status: IProject['status']): string {
    const projectStatus = status as IStatus;
    return projectStatus?.name;
  }

  getCustomerName(c: ICustomer | string | null) {
    return (c as ICustomer)?.name || '';
  }

  getManagerName(m: string | IUser | null) {
    return (m as IUser)?.fullName || 'Unassigned';
  }

  navigateToProject(id: string, archived?: boolean) {
    this.router.navigate(['/projects/dashboard'], { queryParams: { projectId: id, ...(archived ? { archived: 'true' } : {}) } });
  }

  openEditProject(project: IProject) {
    this.editProjectId = project._id;
    this.projectFormData = {
      _id: project._id,
      projectId: project.projectId,
      name: project.name,
      managers: (project.managers as IUser[])?.map(m => m._id) || [],
      customer: this.getCustomerName(project.customer) || '',
      dueDate: project.dueDate ? new Date(project.dueDate) : undefined,
      internal: project.internal ?? false
    };
    // The workspace-users API may not return every project admin (e.g. workspace
    // admins). Merge the project's own managers into the options so each selected
    // admin always has a matching option and never renders as a null entry.
    const missingManagers = ((project.managers as IUser[]) || [])
      .filter(m => m?._id && !this.managerOptions.some(o => o.value === m._id))
      .map(m => ({
        label: m.fullName || m.email || 'Unknown',
        value: m._id,
        email: m.email || '',
        user: m
      }));
    // Reassign (don't mutate in place) so the multi-select's signal input picks it up.
    if (missingManagers.length) {
      this.managerOptions = [...this.managerOptions, ...missingManagers];
    }
    this.projectDialogVisible = true;
  }

  openDeleteDialog(project: IProject) {
    this.projectToDelete = project;
    this.displayDeleteDialog = true;
  }

  confirmDeleteProject() {
    if (!this.projectToDelete) return;
    const project = this.projectToDelete;
    this.projectService.deleteProject(project._id, !!project.active).subscribe(res => {
      if (res.success) {
        this.toast.success('Project deleted successfully.');
        this.projectService.projectsUpdated.next();
        this.displayDeleteDialog = false;
        this.projectToDelete = null;
        this.getProjects(this.first, this.rows);
      } else {
        this.toast.error('Failed to delete project.');
        this.displayDeleteDialog = false;
        this.projectToDelete = null;
      }
    });
  }

  downloadZIP() {
    if (!this.projectToDelete) {
      this.toast.error('Project ID is missing');
      return;
    }

    this.isDownloadingZIP.set(true);
    this.projectService.downloadProjectDocumentsAndKnowledge(this.projectToDelete._id).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.toast.error('Failed to download ZIP file');
          this.isDownloadingZIP.set(false);
          return;
        }

        // Get filename from Content-Disposition header or use default
        let fileName = `${this.projectToDelete?.name}.zip`;

        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.toast.success('ZIP file downloaded successfully');
        this.isDownloadingZIP.set(false);
      },
      error: (error) => {
        console.error('Error downloading ZIP:', error);
        this.toast.error('Failed to download ZIP file');
        this.isDownloadingZIP.set(false);
      }
    });
  }
  onUpdateProject(event: { formData: Partial<IProject>; milestones: IMilestone[]; deletedMilestones: string[]; teamMembers: IShareUser[]; workspaceShare: boolean; projectShare: boolean }) {
    if (!this.editProjectId) return;
    if (this.isUpdateProjectLoading()) return;

    this.isUpdateProjectLoading.set(true);

    this.projectService.updateProject(event.formData, this.editProjectId).subscribe(res => {
      if (res.success) {
        forkJoin([
          this.milestoneService.updateMilestones(event.milestones, event.deletedMilestones, MILESTONE_TYPE.PROJECT, this.editProjectId!),
          this.projectService.updateProjectTeam(this.editProjectId!, event.teamMembers, event.workspaceShare, event.projectShare)
        ]).subscribe({
          next: () => {
            this.projectDialogVisible = false;
            this.resetProjectDialog();
            this.editProjectId = null;
            this.projectService.projectsUpdated.next();
            this.toast.success('Project updated successfully.');
            this.getProjects(this.first, this.rows);
          },
          error: () => {
            this.toast.error('Failed to update project.');
          },
          complete: () => {
            this.isUpdateProjectLoading.set(false);
          }
        });
      } else {
        this.toast.error('Failed to update project.');
        this.isUpdateProjectLoading.set(false);
      }
    });
  }

  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = mode;
  }

  onViewModeChange(mode: 'grid' | 'list') {
    this.setViewMode(mode);
  }

  addUserToShare(shareUser: Partial<IShareUser>) {
    // This method is no longer used as shareDetails is removed
  }

  removeUserFromShare(shareUser: Partial<IShareUser>) {
    // This method is no longer used as shareDetails is removed
  }

  updateSharedUser(shareUser: IShareUser) {
    // This method is no longer used as shareDetails is removed
  }

  changeAnyoneAccess(access: SHARE_ACCESS) {
    // This method is no longer used as shareDetails is removed
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filterProjectName ||
      this.filterCustomerName ||
      this.filterManager.length > 0 ||
      this.filterDueDateRange ||
      this.filterCreatedDateRange ||
      this.projectSearchValue
    );
  }

  getProgressColor(progress: number): string {
    if (progress === 100) {
      return '#22c55e'; // Green for 100%
    } else if (progress > 0) {
      return '#3255fA'; // Blue for >0%
    } else {
      return '#e0e0e0'; // Gray for 0%
    }
  }
}
